import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Keyboard, RotateCcw, Zap } from "lucide-react";
import ShorthandManager from "../components/ShorthandManager";

const DEFAULT_BINDS = [
  { id: "save",      label: "Save document",          default: "Ctrl+S",        category: "Documents" },
  { id: "newDoc",    label: "New document",            default: "Ctrl+N",        category: "Documents" },
  { id: "readMode",  label: "Toggle read mode",        default: "Ctrl+Shift+R",  category: "Documents" },
  { id: "zoomIn",    label: "Zoom in",                 default: "Ctrl+=",        category: "Calendar" },
  { id: "zoomOut",   label: "Zoom out",                default: "Ctrl+-",        category: "Calendar" },
  { id: "today",     label: "Go to today",             default: "T",             category: "Calendar" },
  { id: "newNode",   label: "New node",                default: "N",             category: "Calendar" },
  { id: "splitView", label: "Toggle split view",       default: "Ctrl+\\",       category: "Calendar" },
  { id: "inbox",     label: "Go to inbox",             default: "G I",           category: "Navigation" },
  { id: "calendar",  label: "Go to calendar",          default: "G C",           category: "Navigation" },
  { id: "documents", label: "Go to documents",         default: "G D",           category: "Navigation" },
  { id: "search",    label: "Command palette",         default: "Ctrl+K",        category: "Navigation" },
  { id: "compact",   label: "Compact density",         default: "Ctrl+1",        category: "View" },
  { id: "comfortable","label": "Comfortable density",  default: "Ctrl+2",        category: "View" },
  { id: "spacious",  label: "Spacious density",        default: "Ctrl+3",        category: "View" },
];

export default function KeybindSettings() {
  const nav = useNavigate();
  const [tab, setTab] = useState("keybinds"); // keybinds | shorthands
  const [binds, setBinds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crescendo:keybinds") || "{}"); } catch { return {}; }
  });
  const [recording, setRecording] = useState(null); // id of bind being recorded

  const save = (id, combo) => {
    const next = { ...binds, [id]: combo };
    setBinds(next);
    localStorage.setItem("crescendo:keybinds", JSON.stringify(next));
  };
  const reset = (id) => {
    const next = { ...binds };
    delete next[id];
    setBinds(next);
    localStorage.setItem("crescendo:keybinds", JSON.stringify(next));
  };
  const resetAll = () => { setBinds({}); localStorage.removeItem("crescendo:keybinds"); };

  const categories = [...new Set(DEFAULT_BINDS.map(b => b.category))];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]">
      <div className="border-b border-[#2A2A2A] px-6 py-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex items-center gap-1 bg-[#141414] border border-[#2A2A2A] rounded-lg p-0.5">
          <button onClick={() => setTab("keybinds")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${tab === "keybinds" ? "bg-[#1E1E1E] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
            <Keyboard className="w-3 h-3"/> Shortcuts
          </button>
          <button onClick={() => setTab("shorthands")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${tab === "shorthands" ? "bg-[#1E1E1E] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
            <Zap className="w-3 h-3"/> Shorthands
          </button>
        </div>
        {tab === "keybinds" && (
          <button onClick={resetAll} className="ml-auto flex items-center gap-1.5 text-[11px] text-[#52525B] hover:text-[#A1A1AA]">
            <RotateCcw className="w-3 h-3" /> Reset all
          </button>
        )}
      </div>

      {tab === "shorthands" ? (
        <div className="max-w-2xl mx-auto">
          <ShorthandManager />
        </div>
      ) : (
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {categories.map(cat => (
          <section key={cat}>
            <p className="text-[10px] text-[#52525B] uppercase tracking-widest font-semibold mb-3">{cat}</p>
            <div className="border border-[#2A2A2A] rounded-lg overflow-hidden">
              {DEFAULT_BINDS.filter(b => b.category === cat).map((bind, i, arr) => {
                const current = binds[bind.id] || bind.default;
                const isRecording = recording === bind.id;
                return (
                  <div key={bind.id} className={`flex items-center justify-between px-4 py-2.5 ${i < arr.length - 1 ? "border-b border-[#1a1a1a]" : ""} hover:bg-[#0d0d0d]`}>
                    <span className="text-[12px] text-[#EDEDED]">{bind.label}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (isRecording) { setRecording(null); return; }
                          setRecording(bind.id);
                        }}
                        onKeyDown={isRecording ? (e) => {
                          e.preventDefault();
                          const parts = [];
                          if (e.ctrlKey || e.metaKey) parts.push("Ctrl");
                          if (e.shiftKey) parts.push("Shift");
                          if (e.altKey) parts.push("Alt");
                          if (e.key !== "Control" && e.key !== "Shift" && e.key !== "Alt" && e.key !== "Meta") {
                            parts.push(e.key === " " ? "Space" : e.key.length === 1 ? e.key.toUpperCase() : e.key);
                          }
                          if (parts.length > 0) { save(bind.id, parts.join("+")); setRecording(null); }
                        } : undefined}
                        className={`px-2.5 py-1 rounded font-mono text-[11px] border ${isRecording ? "border-[#5A90F5] bg-[#5A90F5]/10 text-[#5A90F5]" : "border-[#2A2A2A] bg-[#141414] text-[#A1A1AA]"}`}
                      >
                        {isRecording ? "Press keys…" : current}
                      </button>
                      {binds[bind.id] && (
                        <button onClick={() => reset(bind.id)} className="text-[#3F3F46] hover:text-[#52525B]">
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      )}
    </div>
  );
}
